import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { ExternalLink, MapPin, X, type LucideIconData } from 'lucide-angular';

import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { EventCardComponent } from '../event-card/event-card.component';
import { EVENTS_COPY } from '../../constants/events.constants';
import { EventDialogService } from '../../data/event-dialog.service';
import {
  dayLabel,
  eventTypeLabel,
  formatEventPrice,
  formatEventRange,
  multiDayStartTime,
} from '../../utils/event-format.util';

/**
 * Diálogo de eventos, el único de la web (D19). Dos vistas que gobierna
 * `EventDialogService`: **día** (los eventos de un día del calendario) y
 * **detalle** (un evento, con la descripción, la imagen y el enlace externo
 * que la tarjeta ya no lleva).
 *
 * Se monta una sola vez en `app.html`, dentro de un `@defer (when …)`: no entra
 * en el bundle inicial ni se renderiza en SSR (D20). Como el `when` es de un
 * solo disparo, una vez cargado el componente se queda montado y pinta vacío
 * mientras está cerrado.
 *
 * Comportamiento (D23), con el patrón de `ps-delivery-destinations-dialog`
 * —es consulta opcional, así que **sí** cierra con Escape y con el fondo—: focus
 * trap, foco al primer enfocable al abrir y al cambiar de vista, foco devuelto
 * al disparador al cerrar, y bloqueo de scroll del body **restaurando** el
 * `overflow` previo. Todo lo que toca `document` va detrás de `isPlatformBrowser`.
 */
@Component({
  selector: 'app-event-dialog',
  imports: [IconComponent, EventCardComponent],
  templateUrl: './event-dialog.component.html',
  styleUrl: './event-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventDialogComponent {
  private readonly dialog = inject(EventDialogService);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = EVENTS_COPY;
  protected readonly closeIcon: LucideIconData = X;
  protected readonly mapPinIcon: LucideIconData = MapPin;
  protected readonly externalLinkIcon: LucideIconData = ExternalLink;

  private readonly panelRef = viewChild<ElementRef<HTMLElement>>('panel');

  /** `true` solo mientras ESTE componente tiene el scroll bloqueado. */
  private scrollLocked = false;
  private previousBodyOverflow = '';

  protected readonly view = this.dialog.view;

  protected readonly day = computed(() => {
    const view = this.view();
    return view?.kind === 'day' ? view : null;
  });

  protected readonly detail = computed(() => {
    const view = this.view();
    return view?.kind === 'detail' ? view : null;
  });

  protected readonly dayTitle = computed(() => {
    const day = this.day();
    return day ? `${EVENTS_COPY.dayHeadingPrefix} ${dayLabel(day.dayKey)}` : '';
  });

  protected readonly typeLabel = computed(() => {
    const detail = this.detail();
    return detail ? eventTypeLabel(detail.event.type) : '';
  });

  protected readonly dateLabel = computed(() => {
    const detail = this.detail();
    return detail ? formatEventRange(detail.event.startsAt, detail.event.endsAt) : '';
  });

  /** «· desde las {hora}», solo en los de varios días: igual que la tarjeta. */
  protected readonly timeLabel = computed(() => {
    const detail = this.detail();
    return detail ? multiDayStartTime(detail.event.startsAt, detail.event.endsAt) : '';
  });

  protected readonly priceLabel = computed(() => {
    const detail = this.detail();
    return detail ? formatEventPrice(detail.event.price) : '';
  });

  /**
   * URL de la imagen que falló al cargar. Guardar la URL —y no un booleano— hace
   * que el fallo no se herede: otro evento con otra imagen vuelve a intentarlo.
   */
  private readonly failedImageUrl = signal<string | null>(null);

  /** La imagen solo existe si hay `imageUrl` y no ha fallado; si falla, no deja hueco. */
  protected readonly imageUrl = computed(() => {
    const url = this.detail()?.event.imageUrl;
    return url && url !== this.failedImageUrl() ? url : null;
  });

  protected readonly imageAlt = computed(() => {
    const detail = this.detail();
    return detail ? `${EVENTS_COPY.imageAltPrefix} ${detail.event.name}` : '';
  });

  constructor() {
    // Scroll y foco siguen a la vista: al abrir y en cada cambio de vista, foco
    // al primer enfocable (la tarjeta pulsada en la vista día desaparece al
    // pasar al detalle); al cerrar, scroll restaurado y foco al disparador.
    effect(() => {
      const view = this.view();
      if (!this.isBrowser) return;

      if (view) {
        this.lockScroll();
        queueMicrotask(() => this.focusFirst());
      } else {
        this.unlockScroll();
        this.restoreFocus();
      }
    });

    this.destroyRef.onDestroy(() => this.unlockScroll());

    // Escape cierra; Tab/Shift+Tab ciclan dentro del panel. `afterNextRender` ya
    // no corre en el servidor real, pero la guarda lo deja explícito y cubre
    // un `PLATFORM_ID` de servidor en tests.
    afterNextRender(() => {
      if (!this.isBrowser) return;
      fromEvent<KeyboardEvent>(this.document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => this.onKeydown(e));
    });
  }

  protected close(): void {
    this.dialog.close();
  }

  protected back(): void {
    this.dialog.back();
  }

  protected onImageError(url: string): void {
    this.failedImageUrl.set(url);
  }

  private onKeydown(e: KeyboardEvent): void {
    if (!this.dialog.isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    const panel = this.panelRef()?.nativeElement;
    if (!panel) return;

    const focusables = this.focusableElements(panel);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.document.activeElement as HTMLElement | null;

    if (!active || !panel.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private lockScroll(): void {
    if (this.scrollLocked) return;
    // Se guarda el valor previo en vez de forzar '' al cerrar: si algo ya tenía
    // el scroll bloqueado, resetearlo lo reactivaría por debajo.
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
    this.scrollLocked = true;
  }

  private unlockScroll(): void {
    if (!this.scrollLocked) return;
    this.document.body.style.overflow = this.previousBodyOverflow;
    this.scrollLocked = false;
  }

  private focusFirst(): void {
    const panel = this.panelRef()?.nativeElement;
    if (panel) this.focusableElements(panel)[0]?.focus();
  }

  private restoreFocus(): void {
    const trigger = this.dialog.takeTrigger();
    if (!trigger) return;
    queueMicrotask(() => {
      if (this.document.contains(trigger)) trigger.focus({ preventScroll: true });
    });
  }

  /** Enfocables del panel en orden DOM. */
  private focusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === this.document.activeElement,
    );
  }
}
